import { CustomField, MergedBonusItem } from '../../../models/bonuses.model';
import { activePromoStatuses, countBasedPromoTypes, MagicXPromo, PromoTypes } from './types';

export type BonusItemPromptProps = {
  ctaText: string;
  ctaURL?: string;
  title: string;
  paragraphText?: string;
  isCheckboxChecked?: string;
  imgAlt?: string | undefined;
  imgPath: string;
  bonusAmount?: number;
  descriptionTC?: string;
  displayName?: string;
  promotionTitle?: string;
  isHiddenTitle?: boolean;
  promotionCTAType?: string;
  successCTAType?: string;
  bonusType?: string;
  configuration?: {
    BonusPercentage?: number;
    MaximumBonusAmount?: number;
    MinimumDeposit?: string;
    IsPercentage?: boolean;
  } | null;
  promoCode?: string;
  successCTAGameInformation?: {
    gameId?: string;
    gameEnabled?: boolean;
  };
};

type StringUndefined = string | undefined;

export const defaultMergedBonusItem = {
  title: '',
  failedTitle: '',
  successTitle: '',
  failedParagraph: '',
  successParagraph: '',
  failedCTAText: '',
  successCTAText: '',
  failedCTAURL: '',
  successCTAURL: '',
  isFailedWithoutRedirect: '',
  isSuccessWithoutRedirect: '',
  imgAlt: '',
  imgPath: '',
  bonusAmount: 0,
  descriptionTC: '',
  displayName: '',
  promotionTitle: '',
  ctaText: '',
  promotionCTAType: '',
  successCTAType: '',
  bonusType: '',
  configuration: {},
  successCTAGameInformation: {},
};

export const getBonusByPromoCode = (promoCode: string, bonuses: MergedBonusItem[]): MergedBonusItem | undefined => {
  return bonuses.find((promo) => promo.promoCode === promoCode);
};

// TODO: Add strict type def for props
export const getPropsForPrompt = (
  promoCode: string,
  bonuses: MergedBonusItem[],
  isActivateError: boolean
): BonusItemPromptProps => {
  const matchedPromo = getBonusByPromoCode(promoCode, bonuses) || defaultMergedBonusItem;

  const {
    failedTitle,
    successTitle,
    failedParagraph,
    successParagraph,
    failedCTAText,
    successCTAText,
    failedCTAURL,
    successCTAURL,
    isFailedWithoutRedirect,
    isSuccessWithoutRedirect,
    imgAlt = '',
    imgPath = '',
    bonusAmount,
    descriptionTC,
    displayName,
    promotionTitle,
    configuration,
    promotionCTAType,
    successCTAType,
    bonusType,
    successCTAGameInformation,
  } = matchedPromo;

  const handleError = (errorValue: StringUndefined, regularValue: StringUndefined): string =>
    isActivateError ? errorValue || '' : regularValue || '';

  const conditionalProperties = {
    title: handleError(failedTitle, successTitle),
    paragraphText: handleError(failedParagraph, successParagraph),
    ctaText: handleError(failedCTAText, successCTAText),
    ctaURL: handleError(failedCTAURL, successCTAURL),
    isCheckboxChecked: handleError(isFailedWithoutRedirect, isSuccessWithoutRedirect),
  };

  return {
    promoCode,
    imgAlt,
    imgPath,
    bonusAmount,
    descriptionTC,
    displayName,
    promotionTitle,
    configuration,
    promotionCTAType,
    bonusType,
    successCTAType,
    successCTAGameInformation,
    ...conditionalProperties,
  };
};

export const filterFromWorkflow = <T extends { customFields?: CustomField[] }>(bonusItems: T[]): T[] => {
  return bonusItems.filter((item) => {
    return !item.customFields?.some((el) => {
      return el.key.includes('WORKFLOW') && el.value.includes('true');
    });
  });
};

export const isActivePromotion = (status?: string | null): boolean => activePromoStatuses.includes(String(status));

export const isProgressBar = (status?: string | null, promoType?: string | null): boolean => {
  const resPromoType = promoType?.toLowerCase();

  return (
    (resPromoType === PromoTypes.DEPOSIT_MATCH || resPromoType === PromoTypes.WAGER_AND_GET) &&
    isActivePromotion(String(status))
  );
};

export const showCurrency = (type?: string): boolean => !countBasedPromoTypes.includes(Number(type));

export const getMagicXCTA = (magicXPromo: MagicXPromo | undefined): string | undefined =>
  magicXPromo?.values?.gb?.cta?.[0]?.text;

export const compareWithPromoType = (promoType: string | null | undefined, comparedPromoType: PromoTypes): boolean =>
  promoType?.toLocaleLowerCase() === comparedPromoType;
